<template>
  <section class="feedback-admin">
    <header class="block-header">
      <h2>{{ $t('feedback.admin.title') }}</h2>
      <div class="segmented">
        <button type="button" :class="{ active: statusFilter === 'new' }" @click="setStatusFilter('new')">
          {{ $t('feedback.admin.filterNew') }}
        </button>
        <button type="button" :class="{ active: statusFilter === null }" @click="setStatusFilter(null)">
          {{ $t('feedback.admin.filterAll') }}
        </button>
      </div>
      <span class="record-count">{{ $t('referral.admin.total', { count: records.length }) }}</span>
    </header>

    <p v-if="error" class="load-error">{{ error }}</p>
    <p v-if="!records.length && !isLoading" class="empty">{{ $t('feedback.admin.empty') }}</p>

    <article v-for="item in records" :key="item.id" class="feedback-item" :class="{ resolved: item.status === 'resolved' }">
      <header class="item-header">
        <div class="sender">
          <strong>{{ item.accountEmail || item.contactEmail || $t('feedback.admin.anonymous') }}</strong>
          <span v-if="item.contactEmail && item.contactEmail !== item.accountEmail" class="muted">
            {{ $t('feedback.admin.contact') }} {{ item.contactEmail }}
          </span>
          <span class="mono muted">{{ formatTime(item.createdAt) }}</span>
        </div>
        <span class="status">{{ $t(`feedback.admin.status.${item.status}`) }}</span>
        <button type="button" class="filter-btn" :disabled="updatingId === item.id" @click="toggleStatus(item)">
          {{ $t(item.status === 'new' ? 'feedback.admin.markResolved' : 'feedback.admin.markNew') }}
        </button>
      </header>

      <p class="message">{{ item.message }}</p>

      <div v-if="item.screenshotUrls.length" class="shots">
        <a v-for="(url, index) in item.screenshotUrls" :key="url" :href="url" target="_blank" rel="noopener noreferrer">
          <img :src="url" :alt="$t('feedback.screenshotAlt', { index: index + 1 })" loading="lazy">
        </a>
      </div>

      <dl class="context">
        <template v-for="key in CONTEXT_KEYS" :key="key">
          <template v-if="item.context[key] !== undefined">
            <dt>{{ $t(`feedback.admin.context.${key}`) }}</dt>
            <dd :class="{ mono: key !== 'sentenceText' && key !== 'audioName' }">{{ item.context[key] }}</dd>
          </template>
        </template>
      </dl>
    </article>
  </section>
</template>

<script>
import { apiPost } from '@/composables/useApi'

// 展示顺序：先看用户在哪、在练什么，再看环境
const CONTEXT_KEYS = [
  'page', 'view', 'audioName', 'sentenceIndex', 'sentenceText', 'currentTime',
  'audioId', 'materialId', 'locale', 'viewport', 'userAgent',
]

export default {
  name: 'FeedbackAdminPanel',
  props: {
    adminPassword: {
      type: String,
      required: true
    }
  },
  setup() {
    return { CONTEXT_KEYS }
  },
  data() {
    return {
      records: [],
      statusFilter: 'new',
      updatingId: '',
      isLoading: false,
      error: ''
    }
  },
  mounted() {
    this.load()
  },
  methods: {
    adminPost(url, body = {}) {
      return apiPost(url, { adminPassword: this.adminPassword, ...body }, { auth: false })
    },
    async load() {
      this.isLoading = true
      this.error = ''
      try {
        const data = await this.adminPost('/api/admin/feedback', { status: this.statusFilter })
        this.records = data.records
      } catch (error) {
        this.error = error.message
      } finally {
        this.isLoading = false
      }
    },
    setStatusFilter(status) {
      this.statusFilter = status
      this.load()
    },
    async toggleStatus(item) {
      this.updatingId = item.id
      this.error = ''
      try {
        await this.adminPost('/api/admin/feedback/status', {
          id: item.id,
          status: item.status === 'new' ? 'resolved' : 'new'
        })
        await this.load()
      } catch (error) {
        this.error = error.message
      } finally {
        this.updatingId = ''
      }
    },
    formatTime(value) {
      return new Date(value).toLocaleString('zh-CN', { hour12: false })
    }
  }
}
</script>

<style scoped>
.feedback-admin {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.block-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.block-header h2 {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-lg);
  font-weight: 600;
}

.segmented {
  display: inline-flex;
  padding: 2px;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-subtle);
}

.segmented button {
  height: 30px;
  padding: 0 var(--space-3);
  border: 0;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--ink-secondary);
  font: inherit;
  font-size: var(--font-size-sm);
  cursor: pointer;
}

.segmented button.active {
  background: var(--surface-panel);
  color: var(--ink-primary);
  font-weight: 600;
}

.record-count,
.muted {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.mono {
  font-family: var(--font-family-data);
}

.load-error {
  margin: 0;
  color: var(--color-danger);
  font-size: var(--font-size-sm);
}

.empty {
  margin: 0;
  padding: var(--space-8) 0;
  color: var(--ink-tertiary);
  font-size: var(--font-size-sm);
  text-align: center;
}

.feedback-item {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-4);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
}

.feedback-item.resolved {
  background: var(--surface-subtle);
}

.item-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.sender {
  display: flex;
  flex: 1;
  flex-wrap: wrap;
  align-items: baseline;
  gap: var(--space-2);
  min-width: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
}

.status {
  color: var(--ink-secondary);
  font-size: var(--font-size-xs);
}

.filter-btn {
  height: 32px;
  padding: 0 var(--space-3);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--surface-panel);
  color: var(--ink-primary);
  font: inherit;
  font-size: var(--font-size-sm);
  cursor: pointer;
}

.filter-btn:disabled {
  color: var(--ink-tertiary);
  cursor: not-allowed;
}

.segmented button:focus-visible,
.filter-btn:focus-visible,
.shots a:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

.message {
  margin: 0;
  color: var(--ink-primary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
  white-space: pre-wrap;
  overflow-wrap: break-word;
}

.shots {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.shots a {
  display: block;
  border-radius: var(--radius-sm);
}

.shots img {
  display: block;
  width: 180px;
  height: 120px;
  object-fit: cover;
  border: 1px solid var(--border-default);
  border-radius: var(--radius-sm);
  background: var(--surface-subtle);
}

.context {
  display: grid;
  grid-template-columns: max-content minmax(0, 1fr);
  gap: var(--space-1) var(--space-4);
  margin: 0;
  padding-top: var(--space-3);
  border-top: 1px solid var(--border-subtle);
  font-size: var(--font-size-xs);
}

.context dt {
  color: var(--ink-tertiary);
}

.context dd {
  margin: 0;
  color: var(--ink-secondary);
  overflow-wrap: anywhere;
}
</style>
